import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Match, getAllMatches } from '../services/matchService';
import { Button, Alert, CircularProgress, IconButton, Tooltip } from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

const MatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode = (searchParams.get('mode') || localStorage.getItem('matches_view_mode') || 'card') as 'list' | 'card';
  const [viewMode, setViewMode] = useState<'list' | 'card'>(initialMode);

  const fetchMatches = useCallback(async () => {
    try {
      if (!navigator.onLine) {
        setError('Vous êtes actuellement hors ligne. Veuillez vous connecter à Internet pour voir les matchs.');
        setLoading(false);
        return;
      }
      
      const allMatches = await getAllMatches();
      // Trier les matchs par date décroissante
      const sortedMatches = allMatches.sort((a, b) => 
        b.date.toMillis() - a.date.toMillis()
      );
      setMatches(sortedMatches);
      setError(null);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des matchs:', error);
      setLoading(false);
      
      // Gestion des erreurs spécifiques
      if (error instanceof Error) {
        if (error.message.startsWith('NETWORK_ERROR:')) {
          setError(error.message.replace('NETWORK_ERROR: ', ''));
        } else if (error.message.startsWith('AUTH_ERROR:')) {
          setError(error.message.replace('AUTH_ERROR: ', ''));
          // Rediriger vers la page de connexion après un délai
          setTimeout(() => navigate('/login'), 3000);
        } else if (error.message.startsWith('SERVICE_ERROR:')) {
          setError(error.message.replace('SERVICE_ERROR: ', ''));
        } else {
          setError('Une erreur est survenue lors du chargement des matchs. Veuillez réessayer.');
        }
      } else {
        setError('Une erreur inconnue est survenue. Veuillez réessayer ultérieurement.');
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  const handleGoToStats = (matchId: string) => {
    navigate(`/statistiques?id=${matchId}`);
  };

  const handleGoToLiveMatch = (matchId: string) => {
    navigate(`/live-match/${matchId}`);
  };

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === 'card' ? 'list' : 'card'));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-black text-white">
        <CircularProgress color="primary" size={60} thickness={4} />
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-4xl font-bold text-blue-500">Tous les Matchs</h1>
        <Tooltip title={viewMode === 'card' ? 'Vue liste' : 'Vue cartes'}>
          <IconButton onClick={toggleViewMode} color="primary">
            {viewMode === 'card' ? <ViewListIcon /> : <ViewModuleIcon />}
          </IconButton>
        </Tooltip>
      </div>
      
      {error && (
        <div className="mb-6">
          <Alert 
            severity={!isOnline ? "warning" : "error"}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  fetchMatches();
                }}
              >
                Réessayer
              </Button>
            }
          >
            {error}
          </Alert>
        </div>
      )}
      
      {matches.length === 0 ? (
        <div className="text-center text-gray-400 py-8">
          Aucun match trouvé
        </div>
      ) : (
        viewMode === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {matches.map((match) => (
              <div 
                key={match.id} 
                className={`
                  border-2 rounded-xl p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl
                  ${match.status === 'live' ? 'border-red-500 bg-gray-900' : 'border-blue-500 bg-gray-900'}
                `}
              >
                {/* Version mobile */}
                <div className="md:hidden">
                  <div className="flex flex-col items-center mb-4">
                    <h2 className="text-2xl font-semibold text-blue-400 mb-2">
                      {match.teamA && match.teamA.name ? match.teamA.name : 'Mon Équipe'}
                    </h2>
                    <span className="text-3xl font-bold text-white mb-2">
                      {match.scoreA} - {match.scoreB}
                    </span>
                    <h2 className="text-2xl font-semibold text-blue-400">
                      {match.teamB && match.teamB.name ? match.teamB.name : 'Adversaire'}
                    </h2>
                  </div>
                  <div className="text-center mb-4">
                    <span className={`
                      px-3 py-1 rounded-full text-sm font-bold
                      ${match.status === 'live' ? 'bg-green-500 text-white animate-pulse' : 'bg-blue-500'}
                    `}>
                      {match.status === 'live' ? 'LIVE' : 
                       match.status === 'finished' ? '' : 'À VENIR'}
                    </span>
                    <p className="text-sm text-gray-400 mt-2">
                      {new Date(match.date.toMillis()).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {/* Version desktop */}
                <div className="hidden md:block">
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex-1 text-center">
                      <h2 className="text-2xl font-semibold text-blue-400">
                        {match.teamA && match.teamA.name ? match.teamA.name : 'Mon Équipe'}
                      </h2>
                    </div>
                    <div className="mx-4 text-center">
                      <span className={`
                        px-3 py-1 rounded-full text-sm font-bold
                        ${match.status === 'live' ? 'bg-green-500 text-white animate-pulse' : 'bg-blue-500'}
                      `}>
                        {match.status === 'live' ? 'LIVE' : 
                         match.status === 'finished' ? '' : 'À VENIR'}
                      </span>
                    </div>
                    <div className="flex-1 text-center">
                      <h2 className="text-2xl font-semibold text-blue-400">
                        {match.teamB && match.teamB.name ? match.teamB.name : 'Adversaire'}
                      </h2>
                    </div>
                  </div>
                  
                  <div className="text-center mb-6">
                    <span className="text-3xl font-bold text-white">
                      {match.scoreA} - {match.scoreB}
                    </span>
                    <p className="text-sm text-gray-400 mt-2">
                      {new Date(match.date.toMillis()).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-center space-x-4">
                  <button 
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105"
                    onClick={() => handleGoToStats(match.id!)}
                  >
                    Statistiques
                  </button>
                  {match.status === 'live' && (
                    <button 
                      className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all duration-300 transform hover:scale-105"
                      onClick={() => handleGoToLiveMatch(match.id!)}
                    >
                      Live
                    </button>
                  )}

                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-gray-800 rounded-xl overflow-hidden border border-gray-800">
            {matches.map((match, idx) => (
              <div key={match.id} className={`flex flex-col md:flex-row items-start md:items-center justify-between gap-4 px-4 py-4 ${idx % 2 === 0 ? 'bg-gray-900' : 'bg-gray-800'}`}>
                <div className="flex items-center gap-3">
                  <span className={`
                    px-3 py-1 rounded-full text-xs font-bold
                    ${match.status === 'live' ? 'bg-green-500 text-white animate-pulse' : 'bg-blue-500'}
                  `}>
                    {match.status === 'live' ? 'LIVE' : match.status === 'finished' ? '' : 'À VENIR'}
                  </span>
                  <span className="text-sm text-gray-400">
                    {new Date(match.date.toMillis()).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex-1 text-white text-lg font-semibold text-center">
                  {(match.teamA && match.teamA.name ? match.teamA.name : 'Mon Équipe')} 
                  <span className="mx-2 text-gray-400">vs</span>
                  {(match.teamB && match.teamB.name ? match.teamB.name : 'Adversaire')}
                </div>
                <div className="text-white text-xl font-bold text-center w-20">
                  {match.scoreA} - {match.scoreB}
                </div>
                <div className="flex justify-end gap-3 w-full md:w-auto">
                  <button 
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all"
                    onClick={() => handleGoToStats(match.id!)}
                  >
                    Statistiques
                  </button>
                  {match.status === 'live' && (
                    <button 
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all"
                      onClick={() => handleGoToLiveMatch(match.id!)}
                    >
                      Live
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default MatchesPage;
